import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { api, type GeoSettings } from "../api";

// GeoSettingsContext exposes the current geo feature flags (ixp_enabled,
// nix_enabled) to the whole component tree. Components that render geo
// charts or tabs read from here so they react instantly when an admin
// toggles a flag on the Settings page.
//
// Design notes:
//  - `loaded` is separate from `loading`. It is only set true when a
//    successful GET /api/v1/settings/geo response has been received.
//    Until that point the context signals "not yet authoritative" —
//    callers should disable any mutation that spreads `settings` (the
//    DEFAULT placeholder) to avoid accidentally writing wrong values to
//    the server when sfconsole is still starting up after an update.
//  - On failure the fetch retries up to 8 times with exponential back-
//    off (1 s → 2 s → 4 s → … capped at 30 s) so transient restart
//    windows are healed automatically without user interaction.

interface GeoSettingsState {
  settings: GeoSettings;
  loading: boolean;
  /** True only after at least one successful GET from the server. */
  loaded: boolean;
  /** Re-fetch from the server — call after a successful PUT /settings/geo. */
  reload: () => Promise<void>;
}

/** Placeholder — never written to the server (guarded by `loaded`). */
const DEFAULT: GeoSettings = { ixp_enabled: true, nix_enabled: true };

const Ctx = createContext<GeoSettingsState | null>(null);

export function GeoSettingsProvider({ children }: { children: ReactNode }) {
  const [settings, setSettings] = useState<GeoSettings>(DEFAULT);
  const [loading, setLoading]   = useState(true);
  const [loaded, setLoaded]     = useState(false);

  // Retry counter — lives in a ref so retries survive re-renders without
  // triggering them.
  const retryCount = useRef(0);
  const retryTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetch_ = useCallback(async (isRetry = false) => {
    if (!isRetry) {
      retryCount.current = 0;
      setLoading(true);
    }
    try {
      const s = await api.geoSettings();
      setSettings(s);
      setLoaded(true);
      retryCount.current = 0;
    } catch {
      // Server not ready (sfconsole just restarted) — schedule a retry with
      // exponential back-off so the UI heals automatically.
      const attempt = retryCount.current;
      if (attempt < 8) {
        retryCount.current += 1;
        const delay = Math.min(1000 * 2 ** attempt, 30_000); // 1s, 2s, 4s… ≤30s
        retryTimer.current = setTimeout(() => fetch_(true), delay);
      }
      // If all retries exhausted, leave `loaded = false` so the Settings
      // page shows an error state rather than misleading ON toggles.
    } finally {
      if (!isRetry) setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetch_();
    return () => {
      if (retryTimer.current !== null) clearTimeout(retryTimer.current);
    };
  }, [fetch_]);

  // Explicit reload (called after PUT) — cancel any pending retry first.
  const reload = useCallback(async () => {
    if (retryTimer.current !== null) {
      clearTimeout(retryTimer.current);
      retryTimer.current = null;
    }
    await fetch_();
  }, [fetch_]);

  return (
    <Ctx.Provider value={{ settings, loading, loaded, reload }}>
      {children}
    </Ctx.Provider>
  );
}

export function useGeoSettings(): GeoSettingsState {
  const c = useContext(Ctx);
  if (!c) throw new Error("useGeoSettings must be used inside GeoSettingsProvider");
  return c;
}
